import {RouteUrls} from '../constants/RouteUrls';
import {roles} from '../enums/res/roles';
import {AbstractControl, FormControl, FormGroup} from "@angular/forms";

export class Util {

  private static consumedIds = [];

  static formatUrlParams(obj: any[] | null): string {
    let urlParameters = '?';
    for (const objKey of obj) {
      urlParameters += objKey + '=' + obj[objKey];
    }
    return urlParameters;
  }

  public static padNumber(n) {
    return String(n).padStart(7, '0');
  }

  public static unPadNumber(n) {
    return parseInt(n, 10);
  }

  public static unwrapSafely(obj: any, props: string, defaultValue: any = null) {
    const properties = props.split('.');
    let value = obj;
    if (!value) {
      return defaultValue;
    }
    if (!props || properties.length === 0) {
      return obj;
    }
    for (const property of properties) {
      value = value[property];
      if (value === null || value === undefined) {
        return defaultValue;
      }
    }
    return value;
  }

  public static generateRandomId(): number {
    const random = Math.floor(Math.random() * Math.floor(1000000));
    return this.consumedIds.includes(random) ? this.generateRandomId() : random;
  }

  public static getRedirectionUrl(userRoles: any[]): string {
    const rolesToCheck = {
      specialist: roles.ACCREDITATION_SPECIALIST.name,
      facilityAdmin: roles.FACILITY_ADMIN.name,
      billingAdmin: roles.BILLING_ADMIN.name,
      globalAdmin: roles.GLOBAL_ADMIN.name,
      admin: roles.ADMIN.name,
      director: roles.DIRECTOR.name,
      financeSpecialist: roles.FINANCE_SPECIALIST.name,
      financeDirector: roles.FINANCE_DIRECTOR.name,
      surveyor: roles.SURVEYOR.name,
      surveyAdmin: roles.SURVEY_ADMIN.name,
      surveyLead: roles.SURVEYOR_LEAD.name,
      investigator: roles.INVESTIGATOR.name,
      medicalDirector: roles.MEDICAL_DIRECTOR_CLINIC_ADMINISTRATOR.name,
      consultant: roles.CONSULTANT.name,
      accreditationDirector: roles.ACCREDITATION_DIRECTOR.name,
      adminLite: roles.ADMIN_LITE.name,
      facilityContact: roles.FACILITY_CONTACT.name,
      investigationDirector: roles.INVESTIGATION_DIRECTOR.name,
      investigationAdmin: roles.INVESTIGATION_ADMIN.name,
      clinicalReviewer: roles.CLINICAL_REVIEWER.name,
      regulatoryDirector: roles.REGULATORY_DIRECTOR.name,
      staff: roles.STAFF.name
    };
    for (const item of userRoles) {
      if ([rolesToCheck.specialist, rolesToCheck.staff, rolesToCheck.facilityAdmin, rolesToCheck.medicalDirector, rolesToCheck.admin, rolesToCheck.facilityContact].includes(item)) {
        return `/${RouteUrls.APPLICATION.DETAILS_MODULE}`;
      } else if (item === rolesToCheck.consultant || item === rolesToCheck.clinicalReviewer) {
        return `/${RouteUrls.FACILITY_INFO.FACILITY_LIST}`;
      } else if ( item === rolesToCheck.globalAdmin || item === rolesToCheck.director || item === rolesToCheck.accreditationDirector || item === rolesToCheck.financeDirector || item === rolesToCheck.financeSpecialist || item === rolesToCheck.regulatoryDirector) {
        return `/${RouteUrls.ACCOUNT_SETTINGS.MODULE_BASE}/${RouteUrls.ACCOUNT_SETTINGS.USERS}`;
      } else if (item === rolesToCheck.billingAdmin) {
        return `/${RouteUrls.FINANCIAL.INVOICE.MODULE_BASE}/${RouteUrls.FINANCIAL.INVOICE.LIST}`;
      } else if (item === rolesToCheck.surveyor || item === rolesToCheck.surveyAdmin || item === rolesToCheck.surveyLead) {
        return `/${RouteUrls.SURVEY.MODULE_BASE}`;
      } else if (item === rolesToCheck.investigator) {
        return `/${RouteUrls.INVESTIGATION.MODULE_BASE}`;
      } else if (item === rolesToCheck.investigationAdmin || item === rolesToCheck.investigationDirector) {
        return `/${RouteUrls.APPLICATION.MODULE_BASE}`;
      }
      return `/${RouteUrls.APPLICATION.MODULE_BASE}`;
    }
    return `/${RouteUrls.APPLICATION.MODULE_BASE}`;
  }

  /**
   * Check if objects values are the same. Returns true if values are the same.
   * Empty string and null is considered equal.
   * @param objectValues original object values
   * @param objectValuesToCheck object values to check
   */
  public static sameArrayObjectsValues(objectValues: any[], objectValuesToCheck: any[]): boolean {
    if (objectValues == null && objectValuesToCheck == null) {
      return true;
    } else if (objectValues == null || objectValuesToCheck == null) {
      return false;
    }
    if (objectValues.length !== objectValuesToCheck.length) {
      return false;
    }

    for (let i = 0; i < objectValues.length; i++) {
      if (!this.sameObjectValues(objectValues[i], objectValuesToCheck[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if object values including sub object values are the same. Returns true if values are the same.
   * Empty string and null is considered equal.
   * @param objectValues original object values
   * @param objectValuesToCheck object values to check
   */
  public static sameObjectValues(objectValues: any, objectValuesToCheck: any): boolean {
    if (objectValues == null && objectValuesToCheck == null) {
      return true;
    } else if (objectValues == null || objectValuesToCheck == null) {
      return false;
    }

    const objectValuesKeys = Object.keys(objectValues);
    const objectValuesToCheckKeys = Object.keys(objectValuesToCheck);

    if (objectValuesKeys.length !== objectValuesToCheckKeys.length) {
      return false;
    }

    for (const key of objectValuesKeys) {
      if (!objectValuesToCheckKeys.includes(key)) {
        return false;
      }

      if (typeof objectValues[key] === 'object' && typeof objectValuesToCheck[key] === 'object') {
        return this.sameObjectValues(objectValues[key], objectValuesToCheck[key]);
      }
       else if (Array.isArray(objectValues[key]) && Array.isArray(objectValuesToCheck[key])) {
        return this.sameArrayObjectsValues(objectValues[key], objectValuesToCheck[key]);
      }
       else {
        if ((objectValues[key] === null && objectValuesToCheck[key] === '')
          || (objectValues[key] === '' && objectValuesToCheck[key] === null)) {
          return true;
        }
        if (objectValues[key] !== objectValuesToCheck[key]) {
          return false;
        }
      }
    }

    return true;
  }

  public static addCurrentTimeToDate(date: Date): Date {
    if (date) {
      const currentTime = new Date();
      const newDate = new Date(date);
      newDate.setHours(currentTime.getHours());
      newDate.setMinutes(currentTime.getMinutes());
      newDate.setSeconds(currentTime.getSeconds());

      return newDate;
    }
    return null;
  }

  /**
   * Parses API date-only strings (yyyy-MM-dd) as local calendar dates.
   * Avoids UTC interpretation from new Date('yyyy-MM-dd') which shifts the day in US timezones.
   */
  public static parseLocalDate(dateValue: string | Date): Date | null {
    if (!dateValue) {
      return null;
    }
    if (dateValue instanceof Date) {
      return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    }
    const [year, month, day] = dateValue.split('T')[0].split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }
}
